// Per-project automation rule management section — rendered inside ProjectSettingsPanel.
// Features: list rules (trigger → action summary), enabled toggle, delete with confirm.
// Add-rule form: from/to status pickers (with "Alle"=null wildcard), action_type select,
// per-action param inputs (assign_user uses project members dropdown, set_custom_field
// uses custom-field key dropdown, set_priority uses fixed select, add_comment textarea).
// trigger_type is fixed 'status_transition' and never shown as a selector.

import { useState } from "preact/hooks";
import { Plus, Trash2 } from "lucide-preact";
import type { ApiAutomationRule } from "@juuplan/shared";
import { toastSuccess } from "../lib/toast";
import {
  useAutomationRules,
  addAutomationRule,
  patchAutomationRule,
  removeAutomationRule,
} from "../store/automationRules";
import { useStatuses } from "../store/statuses";
import { useMembers } from "../store/members";
import { useCustomFields } from "../store/customFields";

import { Button } from "./ui/Button";

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

type ActionType = "assign_user" | "add_comment" | "set_custom_field" | "set_priority";
type Priority = "low" | "medium" | "high" | "urgent";

const ACTION_LABELS: Record<ActionType, string> = {
  assign_user: "Nutzer zuweisen",
  add_comment: "Kommentar hinzufügen",
  set_custom_field: "Benutzerdefiniertes Feld setzen",
  set_priority: "Priorität setzen",
};

const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Niedrig",
  medium: "Mittel",
  high: "Hoch",
  urgent: "Dringend",
};

const PRIORITY_VALUES: Priority[] = ["low", "medium", "high", "urgent"];

const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2";
const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2";

/**
 * Renders a human-readable one-line summary for a rule's action.
 */
function actionSummary(rule: ApiAutomationRule): string {
  switch (rule.action_type) {
    case "assign_user":
      return `Nutzer zuweisen (ID: ${String(rule.action_params.user_id ?? "?")})`;
    case "add_comment":
      return `Kommentar: „${String(rule.action_params.body ?? "").slice(0, 40)}"`;
    case "set_custom_field":
      return `Feld „${String(rule.action_params.key ?? "?")}" = „${String(rule.action_params.value ?? "")}"`;
    case "set_priority":
      return `Priorität: ${PRIORITY_LABELS[rule.action_params.priority as Priority] ?? String(rule.action_params.priority)}`;
    default:
      return rule.action_type;
  }
}

/**
 * Per-project automation rule management list.
 * Renders inside ProjectSettingsPanel as a section below WorkItemTypesSection.
 */
export function AutomationRulesSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { items, loading, error } = useAutomationRules(projectId);
  const statusesState = useStatuses(projectId);
  const membersState = useMembers(projectId);
  const customFieldsState = useCustomFields(projectId);

  // Rows pending a delete confirm
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());
  // Rows being mutated (toggle/delete)
  const [busy, setBusy] = useState<Set<number>>(new Set());

  // Add-rule form
  const [showNew, setShowNew] = useState(false);
  const [newFromStatus, setNewFromStatus] = useState<string>("");
  const [newToStatus, setNewToStatus] = useState<string>("");
  const [newActionType, setNewActionType] = useState<ActionType>("assign_user");
  const [newEnabled, setNewEnabled] = useState(true);
  // Action-specific params
  const [newUserId, setNewUserId] = useState<string>("");
  const [newCommentBody, setNewCommentBody] = useState("");
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("medium");
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const setBusyId = (id: number, on: boolean): void => {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleEnabled = async (rule: ApiAutomationRule): Promise<void> => {
    setBusyId(rule.id, true);
    try {
      await patchAutomationRule(rule.id, projectId, { enabled: !rule.enabled });
      toastSuccess(rule.enabled ? "Regel deaktiviert." : "Regel aktiviert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setBusyId(rule.id, false);
    }
  };

  const handleDelete = async (rule: ApiAutomationRule): Promise<void> => {
    setBusyId(rule.id, true);
    try {
      await removeAutomationRule(rule.id, projectId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(rule.id);
        return next;
      });
      toastSuccess("Regel gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setBusyId(rule.id, false);
    }
  };

  const resetNewForm = (): void => {
    setNewFromStatus("");
    setNewToStatus("");
    setNewActionType("assign_user");
    setNewEnabled(true);
    setNewUserId("");
    setNewCommentBody("");
    setNewFieldKey("");
    setNewFieldValue("");
    setNewPriority("medium");
  };

  const buildActionParams = (): Record<string, unknown> | null => {
    switch (newActionType) {
      case "assign_user": {
        const uid = parseInt(newUserId, 10);
        if (!newUserId || isNaN(uid)) return null;
        return { user_id: uid };
      }
      case "add_comment": {
        if (!newCommentBody.trim()) return null;
        return { body: newCommentBody.trim() };
      }
      case "set_custom_field": {
        if (!newFieldKey.trim()) return null;
        return { key: newFieldKey.trim(), value: newFieldValue };
      }
      case "set_priority": {
        return { priority: newPriority };
      }
      default:
        return null;
    }
  };

  const submitNew = async (): Promise<void> => {
    const action_params = buildActionParams();
    if (!action_params) return;
    setCreating(true);
    try {
      await addAutomationRule(projectId, {
        trigger_type: "status_transition",
        from_status_id: newFromStatus ? parseInt(newFromStatus, 10) : null,
        to_status_id: newToStatus ? parseInt(newToStatus, 10) : null,
        action_type: newActionType,
        action_params,
        enabled: newEnabled,
      });
      resetNewForm();
      setShowNew(false);
      toastSuccess("Regel angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  const isSubmittable = (): boolean => {
    switch (newActionType) {
      case "assign_user":
        return newUserId.trim().length > 0 && !isNaN(parseInt(newUserId, 10));
      case "add_comment":
        return newCommentBody.trim().length > 0;
      case "set_custom_field":
        return newFieldKey.trim().length > 0;
      case "set_priority":
        return true;
      default:
        return false;
    }
  };

  const statusName = (id: number | null): string => {
    if (id === null) return "Alle";
    const s = statusesState.items.find((x) => x.id === id);
    return s ? s.name : `#${id}`;
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Automatisierung
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Automatisierungsregeln.
        </p>
      )}

      {!loading &&
        items.map((rule) => {
          const isBusy = busy.has(rule.id);
          const isConfirmDel = confirmDel.has(rule.id);

          return (
            <div
              key={rule.id}
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
            >
              {/* Rule summary row */}
              <div class="flex items-start gap-2 px-2 py-1.5">
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-[color:var(--color-on-surface)] truncate">
                    <span class="text-[color:var(--color-on-surface-variant)]">Wenn Status: </span>
                    <span class="font-medium">{statusName(rule.from_status_id)}</span>
                    <span class="text-[color:var(--color-on-surface-variant)]"> → </span>
                    <span class="font-medium">{statusName(rule.to_status_id)}</span>
                  </p>
                  <p class="text-label text-[color:var(--color-on-surface-variant)] truncate mt-0.5">
                    <span>dann </span>
                    <span class="font-medium text-[color:var(--color-on-surface)]">
                      {actionSummary(rule)}
                    </span>
                  </p>
                </div>

                {/* Enabled toggle */}
                <button
                  type="button"
                  disabled={!canEdit || isBusy}
                  onClick={() => void toggleEnabled(rule)}
                  aria-pressed={rule.enabled}
                  aria-label={rule.enabled ? "Regel deaktivieren" : "Regel aktivieren"}
                  title={rule.enabled ? "Aktiv — klicken zum Deaktivieren" : "Inaktiv — klicken zum Aktivieren"}
                  class={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-40 ${
                    rule.enabled
                      ? "bg-[color:var(--color-primary)]"
                      : "bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)]"
                  }`}
                >
                  <span
                    class={`inline-block h-3 w-3 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
                      rule.enabled ? "translate-x-3.5" : "translate-x-0.5"
                    }`}
                  />
                </button>

                {/* Delete button (canEdit only) */}
                {canEdit && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(rule.id);
                        return next;
                      })
                    }
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Regel löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* Delete confirm */}
              {isConfirmDel && canEdit && (
                <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                  <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                    Regel wirklich löschen?
                  </p>
                  <div class="flex items-center gap-1.5 justify-end">
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmDel((prev) => {
                          const next = new Set(prev);
                          next.delete(rule.id);
                          return next;
                        })
                      }
                      class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <Button
                      variant="destructive"
                      size="xs"
                      type="button"
                      onClick={() => void handleDelete(rule)}
                      disabled={isBusy}
                    >
                      {isBusy ? "Lösche…" : "Löschen"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Add new rule */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Regel hinzufügen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neue Regel
              </div>

              {/* Trigger: from status */}
              <div class="flex flex-col gap-0.5">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Wenn Status (von)
                </label>
                <select
                  value={newFromStatus}
                  onChange={(e) => setNewFromStatus((e.target as HTMLSelectElement).value)}
                  class={SELECT_CLS}
                  aria-label="Von-Status"
                >
                  <option value="">Alle</option>
                  {statusesState.items.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trigger: to status */}
              <div class="flex flex-col gap-0.5">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Wenn Status (nach)
                </label>
                <select
                  value={newToStatus}
                  onChange={(e) => setNewToStatus((e.target as HTMLSelectElement).value)}
                  class={SELECT_CLS}
                  aria-label="Zu-Status"
                >
                  <option value="">Alle</option>
                  {statusesState.items.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action type */}
              <div class="flex flex-col gap-0.5">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Aktion
                </label>
                <select
                  value={newActionType}
                  onChange={(e) => {
                    setNewActionType((e.target as HTMLSelectElement).value as ActionType);
                  }}
                  class={SELECT_CLS}
                  aria-label="Aktionstyp"
                >
                  {(Object.keys(ACTION_LABELS) as ActionType[]).map((at) => (
                    <option key={at} value={at}>
                      {ACTION_LABELS[at]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Per-action params */}
              {newActionType === "assign_user" && (
                <div class="flex flex-col gap-0.5">
                  <label class="text-label text-[color:var(--color-on-surface-variant)]">
                    Nutzer
                  </label>
                  {membersState.items.length > 0 ? (
                    <select
                      value={newUserId}
                      onChange={(e) => setNewUserId((e.target as HTMLSelectElement).value)}
                      class={SELECT_CLS}
                      aria-label="Nutzer auswählen"
                    >
                      <option value="">Nutzer wählen…</option>
                      {membersState.items.map((m) => (
                        <option key={m.user.id} value={String(m.user.id)}>
                          {m.user.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      value={newUserId}
                      min={1}
                      onInput={(e) => setNewUserId((e.target as HTMLInputElement).value)}
                      placeholder="User-ID"
                      class={INPUT_CLS}
                      aria-label="User-ID"
                    />
                  )}
                </div>
              )}

              {newActionType === "add_comment" && (
                <div class="flex flex-col gap-0.5">
                  <label class="text-label text-[color:var(--color-on-surface-variant)]">
                    Kommentar
                  </label>
                  <textarea
                    value={newCommentBody}
                    onInput={(e) => setNewCommentBody((e.target as HTMLTextAreaElement).value)}
                    placeholder="Kommentartext…"
                    rows={3}
                    class={`${INPUT_CLS} resize-none`}
                    aria-label="Kommentartext"
                  />
                </div>
              )}

              {newActionType === "set_custom_field" && (
                <>
                  <div class="flex flex-col gap-0.5">
                    <label class="text-label text-[color:var(--color-on-surface-variant)]">
                      Feld
                    </label>
                    {customFieldsState.items.length > 0 ? (
                      <select
                        value={newFieldKey}
                        onChange={(e) => setNewFieldKey((e.target as HTMLSelectElement).value)}
                        class={SELECT_CLS}
                        aria-label="Benutzerdefiniertes Feld"
                      >
                        <option value="">Feld wählen…</option>
                        {customFieldsState.items.map((cf) => (
                          <option key={cf.id} value={cf.key}>
                            {cf.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={newFieldKey}
                        onInput={(e) => setNewFieldKey((e.target as HTMLInputElement).value)}
                        placeholder="Feldschlüssel"
                        class={INPUT_CLS}
                        aria-label="Feldschlüssel"
                      />
                    )}
                  </div>
                  <div class="flex flex-col gap-0.5">
                    <label class="text-label text-[color:var(--color-on-surface-variant)]">
                      Wert
                    </label>
                    <input
                      type="text"
                      value={newFieldValue}
                      onInput={(e) => setNewFieldValue((e.target as HTMLInputElement).value)}
                      placeholder="Wert"
                      class={INPUT_CLS}
                      aria-label="Feldwert"
                    />
                  </div>
                </>
              )}

              {newActionType === "set_priority" && (
                <div class="flex flex-col gap-0.5">
                  <label class="text-label text-[color:var(--color-on-surface-variant)]">
                    Priorität
                  </label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority((e.target as HTMLSelectElement).value as Priority)}
                    class={SELECT_CLS}
                    aria-label="Priorität"
                  >
                    {PRIORITY_VALUES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Enabled toggle */}
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-rule-enabled"
                  checked={newEnabled}
                  onChange={(e) => setNewEnabled((e.target as HTMLInputElement).checked)}
                  class="rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                />
                <label
                  for="new-rule-enabled"
                  class="text-xs text-[color:var(--color-on-surface)]"
                >
                  Regel sofort aktivieren
                </label>
              </div>

              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    resetNewForm();
                    setShowNew(false);
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!isSubmittable() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
